import { BreakPointCondition, UI, useBreakpointIf, HStack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React from 'react';
import { FlatList } from 'react-native';

import { FeatureToggle, useUnleashFlags } from '../../../../../../utils/useUnleashFlags';
import { OrderEntityWithEvents } from '../../../OrderDetails/constants';
import { OrderItem, OrderItemProps } from '../OrderItem';
import { MobileSeparator, Separator } from '../Separator';

export declare namespace AssetDetailsTypes {
  export interface GetterProps<T> {
    assetExtraInfoGetter: (order: T) => OrderItemProps[][];
    assetInfoGetter: (order: T) => OrderItemProps[];
    assetPreInfoGetter: (order: T) => OrderItemProps[];
    assetInfoColumnsGetter: (order: T) => number | undefined;
  }
  export interface Props<T> extends GetterProps<T> {
    isMobile?: boolean;
    assetTypeLabel: string;
    assetTotalValue: string;
    orders: T[];
  }
}

export const AssetDetails = <T,>({
  orders,
  isMobile,
  assetTypeLabel,
  assetTotalValue,
  assetInfoColumnsGetter,
  assetInfoGetter,
  assetPreInfoGetter,
  assetExtraInfoGetter,
}: AssetDetailsTypes.Props<T>) => {
  const { b2bShowOrderHistoryEquityActions } = useUnleashFlags(
    FeatureToggle.OrderHistoryEquityActions
  );

  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');
  const preAssetInfo = assetPreInfoGetter(orders[0]);
  const viewportType = useBreakpointValue({
    base: 'mobile',
    sm: 'tablet',
    lg: 'desktop',
  }) as OrderEntityWithEvents['viewportType'];

  const renderExtraInfo = (extraInfoRows: OrderItemProps[]) => {
    const standardRows = isTablet ? 2 : 1;
    const sellBuyRows = 1;
    const rows = b2bShowOrderHistoryEquityActions ? sellBuyRows : standardRows;
    const columns = isMobile ? 1 : extraInfoRows.length / rows;
    const oneColumn = columns === 1;

    return (
      <HStack
        bgColor={Color.background}
        px={Spacing['inline.xxs']}
        py={Spacing['stack.xxxs']}
        borderRadius={BoxBorder.heavy}
        w="full">
        <FlatList
          key={`extraInfoFlatList-${isTablet}-${columns}`}
          data={extraInfoRows}
          {...(!oneColumn && {
            columnWrapperStyle: {
              justifyContent: rows === 1 ? 'space-between' : undefined,
              paddingVertical: Utils.Token.ToInt(Spacing, 'stack.nano'),
            },
          })}
          {...(!!oneColumn && { ItemSeparatorComponent: MobileSeparator })}
          style={{ width: '100%' }}
          numColumns={columns}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => (
            <OrderItem
              {...item}
              horizontal={isMobile}
              w={`${100 / columns}%`}
              maxW={`${100 / columns}%`}
            />
          )}
        />
      </HStack>
    );
  };

  const renderProductInfo = (order: T) => {
    const orderWithViewportType = { ...order, viewportType };
    const assetInfo = assetInfoGetter(orderWithViewportType);
    const assetInfoColumns = assetInfoColumnsGetter(orderWithViewportType);
    const assetExtraInfo = assetExtraInfoGetter(order);

    const columns = isMobile ? 1 : assetInfoColumns || assetInfo.length;
    const rows = Math.ceil(assetInfo.length / columns);
    const oneColumn = columns === 1;

    return (
      <VStack space={Spacing['inset.xs']}>
        <HStack
          px={Spacing['inline.xxs']}
          py={Spacing['stack.xxxs']}
          w="full"
          borderRadius={BoxBorder.heavy}
          bg={Color.background}>
          <FlatList
            key={`infoFlatList-${columns}`}
            data={assetInfo}
            {...(!oneColumn && {
              columnWrapperStyle: {
                justifyContent: rows === 1 ? 'space-between' : undefined,
                paddingVertical: Utils.Token.ToInt(Spacing, 'stack.nano'),
              },
            })}
            {...(!!oneColumn && { ItemSeparatorComponent: MobileSeparator })}
            style={{ width: '100%' }}
            numColumns={columns}
            showsVerticalScrollIndicator={false}
            renderItem={({ item }) => (
              <OrderItem
                {...item}
                horizontal={isMobile}
                w={`${100 / columns}%`}
                maxW={`${100 / columns}%`}
              />
            )}
          />
        </HStack>
        <FlatList
          data={assetExtraInfo}
          style={{
            borderRadius: Utils.Token.ToInt(BoxBorder, 'heavy'),
            backgroundColor: Color.background,
          }}
          ItemSeparatorComponent={() => <Separator />}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => renderExtraInfo(item)}
        />
      </VStack>
    );
  };

  return (
    <VStack space={Spacing['stack.xxxs']} p={Spacing['inset.sm']} pt={0}>
      <HStack
        justifyContent="space-between"
        bgColor={Color.backgroundlight}
        px={Spacing['inline.xxxs']}
        py={Spacing['stack.nano']}
        borderRadius={BoxBorder.heavy}>
        <UI.Text variant="semibold" color="neutral.dark">
          {assetTypeLabel}
        </UI.Text>
        <UI.Text variant="semibold" color="neutral.dark">
          Total {assetTotalValue}
        </UI.Text>
      </HStack>
      {!!preAssetInfo.length && (
        <VStack
          px={Spacing['inline.xxs']}
          py={Spacing['stack.xxxs']}
          space={Spacing['stack.nano']}
          bgColor={Color.backgroundlight}>
          {preAssetInfo.map((item) => (
            <OrderItem
              key={item.label}
              {...item}
              horizontal
              valueTextProps={{ fontSize: Typography['fontsize.xs'] }}
            />
          ))}
        </VStack>
      )}
      <FlatList
        data={orders}
        ItemSeparatorComponent={({ leadingItem }) => {
          const hasExtraInfo = !!leadingItem && !!assetExtraInfoGetter(leadingItem).length;
          return <Separator hasExtraInfo={hasExtraInfo} noPadding={hasExtraInfo} />;
        }}
        keyExtractor={(_, index) => `${assetTypeLabel}-${index}`}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => renderProductInfo(item)}
      />
    </VStack>
  );
};
